<template>
  <div class="card shadow-sm p-3">
    <div class="d-flex align-items-center gap-2 mb-3">
      <span class="material-icons text-secondary">analytics</span>
      <span class="fw-bold">Formulario 012B — Planilla {{ numero }}</span>
    </div>

    <button
      class="btn btn-secondary btn-sm text-white d-flex align-items-center gap-1"
      @click="validar"
      :disabled="cargando"
    >
      <span v-if="cargando" class="spinner-border spinner-border-sm"></span>
      <span v-else class="material-icons" style="font-size:16px">verified</span>
      {{ cargando ? 'Validando...' : 'Validar 012B' }}
    </button>

    <!-- Error archivo -->
    <div v-if="errorMensaje" class="alert alert-danger small mt-3">
      <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">error</span>
      {{ errorMensaje }}
    </div>

    <div v-if="resultado" class="mt-3">
      <hr />

      <!-- Alerta resumen -->
      <div class="alert py-2 small" :class="alertaClase">
        <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">
          {{ todoOk ? 'check_circle' : 'warning' }}
        </span>
        {{ todoOk
            ? 'El formulario 012B es válido.'
            : 'Se encontraron diferencias en el formulario 012B.' }}
      </div>

      <!-- ── Tabla comparación ───────────────────────────────────────────────── -->
      <table class="table table-sm table-bordered small">
        <thead class="table-dark">
          <tr>
            <th>Campo</th>
            <th>Base de datos</th>
            <th>Formulario 012B</th>
            <th style="width:50px" class="text-center">OK</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="fila in filasComparacion" :key="fila.campo"
              :class="fila.coincide === false ? 'table-danger' : ''">
            <td class="fw-bold">{{ fila.campo }}</td>
            <td>{{ fila.bd ?? '-' }}</td>
            <td>{{ fila.pdf ?? '-' }}</td>
            <td class="text-center">
              <span v-if="fila.coincide === true"  class="badge bg-success">✓</span>
              <span v-else-if="fila.coincide === false" class="badge bg-danger">✗</span>
              <span v-else class="text-muted small">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ── Hallazgos e impresión diagnóstica ──────────────────────────────── -->
      <p class="fw-bold small mb-1 mt-3">Hallazgos imagenológicos</p>
      <div class="border rounded p-2 small bg-light mb-2" style="white-space: pre-line;">
        {{ resultado.hallazgosPdf ?? '-' }}
      </div>

      <div class="d-flex align-items-start gap-2 small mb-3">
        <span class="material-icons text-primary" style="font-size:16px">summarize</span>
        <div>
          <span class="fw-bold">Impresión diagnóstica: </span>
          <span>{{ resultado.impresionDiagnosticaPdf ?? '-' }}</span>
        </div>
      </div>

      <!-- ── Datos del servicio ─────────────────────────────────────────────── -->
      <p class="fw-bold small mb-1 mt-3">Datos del servicio</p>
      <div class="row g-1 small mb-3">
        <div class="col-6">
          <span class="text-muted fw-bold">Profesional que realiza: </span>
          <span>{{ resultado.profesionalRealizaPdf ?? '-' }}</span>
        </div>
        <div class="col-6">
          <span class="text-muted fw-bold">Fecha realización: </span>
          <span>{{ resultado.fechaRealizacionPdf ?? '-' }}</span>
        </div>
        <div class="col-12">
          <span class="text-muted fw-bold">Profesional solicitante: </span>
          <span>{{ resultado.profesionalSolicitaPdf ?? '-' }}</span>
        </div>
        <div class="col-12" v-if="resultado.descripcionEstudioPdf">
          <span class="text-muted fw-bold">Estudio realizado: </span>
          <span>{{ resultado.descripcionEstudioPdf }}</span>
        </div>
      </div>

      <!-- ── Profesional responsable ───────────────────────────────────────── -->
      <p class="fw-bold small mb-1 mt-3">Profesional responsable (firma)</p>
      <div class="row g-2 small">
        <div class="col-6">
          <label class="fw-bold">Nombre</label>
          <input type="text" class="form-control form-control-sm"
                 :value="resultado.nombreProfesional" readonly />
        </div>
        <div class="col-3">
          <label class="fw-bold">Cédula</label>
          <input type="text" class="form-control form-control-sm"
                 :value="resultado.cedulaProfesional" readonly />
        </div>
        <div class="col-3">
          <label class="fw-bold">Fecha / Hora</label>
          <input type="text" class="form-control form-control-sm"
                 :value="resultado.fechaPdf && resultado.horaPdf
                          ? `${resultado.fechaPdf} ${resultado.horaPdf}`
                          : resultado.fechaPdf ?? '-'" readonly />
        </div>
      </div>

      <!-- ── Texto colapsable ───────────────────────────────────────────────── -->
      <div class="mt-3">
        <button class="btn btn-outline-secondary btn-sm"
                @click="mostrarTexto = !mostrarTexto">
          <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">
            {{ mostrarTexto ? 'expand_less' : 'expand_more' }}
          </span>
          {{ mostrarTexto ? 'Ocultar texto' : 'Ver texto completo del PDF' }}
        </button>
        <textarea v-if="mostrarTexto"
                  class="form-control form-control-sm mt-2"
                  rows="6" :value="resultado.textoCompleto" readonly />
      </div>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "Formulario012BValidador",

  props: {
    idTramite:   { type: Number,  required: true },
    numero:      { type: String,  required: true },
    autoValidar: { type: Boolean, default: false },
  },

  emits: ['resultado'],

  data() {
    return {
      resultado:    null,
      cargando:     false,
      errorMensaje: null,
      mostrarTexto: false,
    };
  },

  computed: {
    todoOk() {
      return this.resultado?.cedulaCoincide
          && this.resultado?.nombreCoincide
          && this.resultado?.fechaEnRango;
    },

    alertaClase() {
      return this.todoOk ? 'alert-success' : 'alert-warning';
    },

    filasComparacion() {
      if (!this.resultado) return [];
      return [
        // ── Con comparación ───────────────────────────────────────────────────
        {
          campo:    'Cédula',
          bd:       this.resultado.cedulaBd,
          pdf:      this.resultado.cedulaPdf,
          coincide: this.resultado.cedulaCoincide,
        },
        {
          campo:    'Nombre',
          bd:       this.resultado.nombreBd,
          pdf:      this.resultado.nombrePdf,
          coincide: this.resultado.nombreCoincide,
        },
        {
          campo:    'Fecha realización (rango BD)',
          bd:       `${this.resultado.fechaDesdeBd ?? '-'}  →  ${this.resultado.fechaHastaBd ?? '-'}`,
          pdf:      this.resultado.fechaRealizacionPdf,
          coincide: this.resultado.fechaEnRango,
        },
        // ── Solo informativos ─────────────────────────────────────────────────
        {
          campo:    'Institución',
          bd:       '-',
          pdf:      this.resultado.institucionPdf,
          coincide: null,
        },
        {
          campo:    'Establecimiento',
          bd:       '-',
          pdf:      this.resultado.establecimientoPdf,
          coincide: null,
        },
        {
          campo:    'No. HCU',
          bd:       '-',
          pdf:      this.resultado.numeroHcuPdf,
          coincide: null,
        },
      ];
    },
  },

  mounted() {
    if (this.autoValidar) this.validar();
  },

  methods: {
    async ejecutarValidacion() {
      await this.validar();
    },

    async validar() {
      this.cargando     = true;
      this.resultado    = null;
      this.errorMensaje = null;
      this.mostrarTexto = false;

      try {
        const resp = await api.get(
          `/validacion-012b/${this.idTramite}/${this.numero}`);
        const v = resp.data;

        if (!v.archivoEncontrado) {
          this.errorMensaje = v.mensajeError ?? "Archivo 012B.pdf no encontrado.";
          this.$emit('resultado', 'sin-archivo');
          return;
        }

        this.resultado = v;
        this.$emit('resultado', this.calcularEstado());
      } catch (e) {
        console.error(e);
        Swal.fire({
          icon:  "error",
          title: "Error",
          text:  "No se pudo validar el formulario 012B.",
        });
      } finally {
        this.cargando = false;
      }
    },

    calcularEstado() {
      if (!this.resultado) return null;
      if (!this.resultado.archivoEncontrado) return 'sin-archivo';

      const hayError =
        !this.resultado.cedulaCoincide ||
        !this.resultado.nombreCoincide ||
        !this.resultado.fechaEnRango;

      if (hayError) return 'error';
      return 'ok';
    },
  },
};
</script>